import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Form, Divider, Spin, Row, Col } from 'antd';
import type { FormInstance } from 'antd/lib/form';
import { CreateEditTrainingProjectFormValues } from '../../types';
import {
    TrainingProjectNameInput,
    TrainingProjectModelsSettings,
    TrainingProjectTaskTypeSelect,
    TrainingProjectOptimizerTypeSelect,
    TrainingProjectOptimizerParamsInput,
    TrainingProjectJsonSchemas,
    TrainingProjectFieldDescriptions,
    TrainingProjectModuleTypeSelect,
    TrainingProjectAdapterTypeSelect
} from '..';
import { TrainingProjectDashboardStore, TrainingProjectsStore } from '../../stores';
import './TrainingProjectSettings.less';

interface Props {
    store: TrainingProjectDashboardStore;
    trainingProjectsStore: TrainingProjectsStore;
    formRef: React.MutableRefObject<FormInstance<CreateEditTrainingProjectFormValues> | null>;
    onSubmit: (formValues: CreateEditTrainingProjectFormValues) => Promise<void>;
    onFormValuesChange?: () => void;
}

/**
 * TrainingProjectSettings component allows users to update training project settings
 * directly from the dashboard without navigating back to the main list.
 *
 * This component includes all the necessary form fields for configuring a training project:
 * - Basic settings (name)
 * - Model configuration (reference, teacher, judge models and system prompt)
 * - Task type selection
 * - Optimizer settings (type and parameters)
 * - JSON schemas for output validation
 * - Field descriptions for input/output
 *
 * Form values are automatically populated via component initialValue props from editableTrainingProject.
 * The parent TrainingProjectDashboard manages the editable state of the training project.
 */
const TrainingProjectSettings: React.FC<Props> = ({
    store,
    trainingProjectsStore,
    formRef,
    onSubmit,
    onFormValuesChange
}) => {
    const [form] = Form.useForm<CreateEditTrainingProjectFormValues>();

    // Expose form instance to parent component
    React.useEffect(() => {
        if (formRef) {
            formRef.current = form;
        }
    }, [form, formRef]);

    // Show loading spinner only if training project is still loading
    // Don't block on ML models loading since form can work without them initially
    if (store.isLoading || !store.trainingProject) {
        return (
            <div className="training-project-settings-loading">
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="training-project-settings">
            <Form
                form={form}
                layout="vertical"
                onFinish={onSubmit}
                onValuesChange={onFormValuesChange}
                preserve={false}
                scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
                className="training-project-settings-form"
            >
                {/* Basic Information Section */}
                <Row gutter={[0, 16]}>
                    <Col xs={24}>
                        <div className="training-project-settings-section">
                            <TrainingProjectNameInput store={trainingProjectsStore} />
                        </div>
                    </Col>
                    <Col xs={24}>
                        <div className="training-project-settings-section">
                            <TrainingProjectTaskTypeSelect store={trainingProjectsStore} />
                        </div>
                    </Col>
                </Row>

                <Divider />

                {/* Model Configuration Section */}
                <div className="training-project-settings-section">
                    <TrainingProjectModelsSettings store={trainingProjectsStore} />
                </div>

                <Divider />

                {/* Adapter Type Section */}
                <Row gutter={[0, 16]}>
                    <Col xs={24}>
                        <div className="training-project-settings-section">
                            <TrainingProjectAdapterTypeSelect store={trainingProjectsStore} />
                        </div>
                    </Col>
                </Row>

                <Divider />

                {/* Module Type Section */}
                <Row gutter={[0, 16]}>
                    <Col xs={24}>
                        <div className="training-project-settings-section">
                            <TrainingProjectModuleTypeSelect store={trainingProjectsStore} />
                        </div>
                    </Col>
                </Row>

                <Divider />

                {/* Optimizer Configuration Section */}
                <Row gutter={[0, 16]}>
                    <Col xs={24}>
                        <div className="training-project-settings-section">
                            <TrainingProjectOptimizerTypeSelect store={trainingProjectsStore} />
                        </div>
                    </Col>
                    <Col xs={24}>
                        <div className="training-project-settings-section tp-monospace">
                            <TrainingProjectOptimizerParamsInput store={trainingProjectsStore} />
                        </div>
                    </Col>
                </Row>

                <Divider />

                {/* JSON Schemas and Field Descriptions Section */}
                <Row gutter={[0, 24]}>
                    <Col xs={24}>
                        <div className="training-project-settings-section tp-monospace">
                            <TrainingProjectJsonSchemas store={trainingProjectsStore} />
                        </div>
                    </Col>
                    <Col xs={24}>
                        <div className="training-project-settings-section">
                            <TrainingProjectFieldDescriptions store={trainingProjectsStore} />
                        </div>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

export default observer(TrainingProjectSettings);
